
import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import PropTypes from "prop-types";
import LogoFallback from "/img/logo.png";
import axios from "axios";
import he from "he";

import {
  CalendarDays,
  ArrowLeft,
  ArrowRight,
  ArrowUpRight,
  Clock,
  Sparkles,
  CalendarCheck,
  FileText,
  Image as ImageIcon,
  RefreshCw,
  ChevronRight,
  Newspaper,
} from "lucide-react";

// UCTC Brand Colors
const UCTC_BLUE = "#014864";
const UCTC_CYAN = "#6BCCF2";
const UCTC_GREEN = "#115e32";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const formatDate = (dateStr) => {
  if (!dateStr) return "Date TBA";

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) {
    return "Date TBA";
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const getFileMimeType = (url) => {
  if (!url) return "unknown";

  const cleanUrl = url.split("?")[0].split("#")[0];
  const ext = cleanUrl.split(".").pop()?.toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext)) {
    return "image";
  }

  if (ext === "pdf") {
    return "pdf";
  }

  return "unknown";
};

const EventSkeleton = () => (
  <div className="animate-pulse space-y-3 rounded-xl border border-cyan-100 bg-white p-4">
    <div className="h-3 w-1/3 rounded bg-slate-200" />
    <div className="h-4 w-4/5 rounded bg-slate-200" />
    <div className="h-3 w-2/3 rounded bg-slate-100" />
  </div>
);

export default function EventsDetails({
  logoSrc = LogoFallback,
  accent = UCTC_GREEN,
  pageTitle = "Events | University of Creative Technology Chittagong",
}) {
  const { id } = useParams();

  const [allnotice, setAllnotice] = useState([]);
  const [notice, setNotice] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedNotice, setSelectedNotice] = useState(null);

  // API Instance
  const api = axios.create({
    baseURL: import.meta.env.VITE_API_BASE_URL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  // Fetch Event Details
  const handlePost = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/events-details", {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        id,
      });

      const data = Array.isArray(response.data)
        ? response.data
        : response.data
          ? [response.data]
          : [];

      setNotice(data);
      setSelectedNotice(data[0] || null);

      if (!data.length) {
        setError("Event information could not be found.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.message ||
          "Unable to load event details."
      );
    } finally {
      setLoading(false);
    }
  };

  // Fetch Recent Events
  const noticePost = async () => {
    setRecentLoading(true);

    try {
      const response = await api.post("/recent-events", {
        userId: import.meta.env.VITE_API_USER,
        password: import.meta.env.VITE_API_PASS,
        department_id: "0",
      });

      setAllnotice(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (err) {
      console.error("Recent events loading error:", err);
    } finally {
      setRecentLoading(false);
    }
  };

  useEffect(() => {
    handlePost();
    noticePost();
  }, [id]);

  // Update Page Title
  useEffect(() => {
    document.title = selectedNotice?.name
      ? `${he.decode(selectedNotice.name)} | UCTC Events`
      : pageTitle;
  }, [pageTitle, selectedNotice]);

  // Decode HTML entities
  const decodeTitle = (rawTitle) => {
    return he.decode(rawTitle || "");
  };

  // Current Event Image / PDF
  const imageUrl = selectedNotice?.images
    ? `${import.meta.env.VITE_API_SOURCE_URL}${selectedNotice.images}`
    : "";

  const fileType = getFileMimeType(imageUrl);

  // Description Formatting
  const textFormat = (textdata) => {
    if (!textdata) return "";

    let textinfo = textdata;

    textinfo = textinfo.replace(/\\n/g, "<br />");
    textinfo = textinfo.split("\n").join("<br />");
    textinfo = textinfo.split("\\r").join(" ");
    textinfo = textinfo.split("\\").join(" ");

    return textinfo;
  };

  // Select Recent Event
  const selectEvent = (event) => {
    setSelectedNotice(event);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <main className="min-h-screen overflow-hidden bg-gradient-to-br from-[#e0f7fa] via-white to-[#e6f4ea]">
      {/* ================================================== */}
      {/* HERO SECTION */}
      {/* ================================================== */}

      <section className="relative isolate overflow-hidden bg-gradient-to-br from-[#6BCCF2] via-[#e0f7fa] to-[#f0fdf4]">
        {/* Background Decorations */}

        <div className="absolute inset-0 -z-10 overflow-hidden">
          <motion.div
            animate={{
              x: [0, 30, 0],
              y: [0, -20, 0],
            }}
            transition={{
              duration: 12,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -left-32 -top-32 h-96 w-96 rounded-full bg-white/40 blur-3xl"
          />

          <motion.div
            animate={{
              x: [0, -25, 0],
              y: [0, 25, 0],
            }}
            transition={{
              duration: 10,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -right-20 bottom-0 h-96 w-96 rounded-full bg-emerald-200/40 blur-3xl"
          />

          <div
            className="absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "radial-gradient(#014864 1px, transparent 1px)",
              backgroundSize: "30px 30px",
            }}
          />
        </div>

        {/* Floating Shapes */}

        <motion.div
          animate={{
            y: [0, -18, 0],
            rotate: [0, 8, 0],
          }}
          transition={{
            duration: 7,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute right-[12%] top-20 hidden h-20 w-20 rounded-2xl border border-[#014864]/20 bg-white/30 backdrop-blur-md lg:block"
        />

        <motion.div
          animate={{
            y: [0, 20, 0],
            rotate: [0, -10, 0],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute bottom-24 left-[10%] hidden h-14 w-14 rounded-full border border-emerald-500/20 bg-emerald-300/20 lg:block"
        />

        {/* Hero Content */}

        <div className="relative mx-auto max-w-7xl px-6 pb-32 pt-20 md:px-12 md:pb-36 md:pt-28">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            {/* Breadcrumb */}

            <div className="mb-8 flex flex-wrap items-center justify-center gap-2 text-sm text-[#014864]/70 md:justify-start">
              <Link
                to="/"
                className="transition hover:text-[#115e32]"
              >
                Home
              </Link>

              <ChevronRight size={15} />

              <Link
                to="/all-events"
                className="transition hover:text-[#115e32]"
              >
                Events
              </Link>

              <ChevronRight size={15} />

              <span className="font-semibold text-[#014864]">
                Details
              </span>
            </div>

            {/* Badge */}

            <div className="mb-6 flex justify-center md:justify-start">
              <span className="inline-flex items-center gap-2 rounded-full border border-[#014864]/10 bg-white/60 px-5 py-2 text-sm font-semibold text-[#014864] shadow-sm backdrop-blur-md">
                <Sparkles size={16} className="text-[#115e32]" />
                UCTC Campus Events
              </span>
            </div>

            {/* Title */}

            <AnimatePresence mode="wait">
              <motion.div
                key={selectedNotice?.id || "loading"}
                initial={{ opacity: 0, y: 25 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -15 }}
                transition={{ duration: 0.5 }}
              >
                <h1 className="mx-auto max-w-5xl text-center text-3xl font-extrabold leading-tight tracking-tight text-[#014864] sm:text-4xl md:mx-0 md:text-left md:text-6xl">
                  {selectedNotice ? (
                    decodeTitle(selectedNotice.name)
                  ) : loading ? (
                    "Discover Our Events"
                  ) : (
                    "Event Details"
                  )}
                </h1>
              </motion.div>
            </AnimatePresence>

            <p className="mx-auto mt-6 max-w-2xl text-center text-base leading-8 text-slate-700 md:mx-0 md:text-left md:text-lg">
              Discover the stories, celebrations, and memorable
              experiences that make life at UCTC extraordinary.
            </p>

            {/* Event Meta */}

            {selectedNotice && (
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-8 flex flex-wrap justify-center gap-3 md:justify-start"
              >
                <div className="inline-flex items-center gap-2 rounded-xl border border-white/70 bg-white/70 px-4 py-3 text-sm font-semibold text-[#014864] shadow-sm backdrop-blur-md">
                  <CalendarDays
                    size={18}
                    className="text-[#115e32]"
                  />

                  {formatDate(selectedNotice.start_date)}
                </div>

                <div className="inline-flex items-center gap-2 rounded-xl border border-white/70 bg-white/70 px-4 py-3 text-sm font-semibold text-[#014864] shadow-sm backdrop-blur-md">
                  <Clock
                    size={18}
                    className="text-[#115e32]"
                  />

                  {formatDate(selectedNotice.end_date)}
                </div>
              </motion.div>
            )}

            {/* Back Button */}

            <div className="mt-8 flex justify-center md:justify-start">
              <Link
                to="/all-events"
                className="group inline-flex items-center gap-2 rounded-xl bg-[#115e32] px-6 py-3.5 font-bold text-white shadow-lg shadow-emerald-900/10 transition duration-300 hover:-translate-y-1 hover:bg-[#0d4826]"
              >
                <ArrowLeft
                  size={18}
                  className="transition group-hover:-translate-x-1"
                />

                All Events
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Bottom Wave */}

        <div className="absolute bottom-0 left-0 w-full">
          <svg
            viewBox="0 0 1440 100"
            className="h-16 w-full md:h-24"
            preserveAspectRatio="none"
          >
            <path
              d="M0,60 C360,120 900,0 1440,60 L1440,100 L0,100 Z"
              fill="#f8fafc"
            />
          </svg>
        </div>
      </section>

      {/* ================================================== */}
      {/* EVENT DETAILS + RECENT EVENTS */}
      {/* ================================================== */}

      <section className="relative px-4 pb-24 pt-8 sm:px-8 lg:px-12">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-start gap-8 lg:grid-cols-3">
          {/* LEFT: EVENT DETAILS */}

          <motion.div
            initial={{ opacity: 0, x: -35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7 }}
            className="min-w-0 lg:col-span-2"
          >
            <div className="overflow-hidden rounded-3xl border border-cyan-100 bg-white shadow-xl shadow-[#014864]/5">
              {/* Article Header */}

              <div className="border-b border-cyan-50 bg-gradient-to-r from-cyan-50/80 to-emerald-50/60 p-6 sm:p-8">
                <div className="mb-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#115e32]">
                  <span className="h-1 w-7 rounded-full bg-[#115e32]" />

                  Event Information
                </div>

                {selectedNotice ? (
                  <h2 className="text-2xl font-extrabold leading-tight text-[#014864] md:text-3xl">
                    {decodeTitle(selectedNotice.name)}
                  </h2>
                ) : (
                  <div className="h-8 w-3/4 animate-pulse rounded bg-slate-200" />
                )}

                {selectedNotice && (
                  <div className="mt-5 flex flex-wrap items-center gap-3 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-2">
                      <CalendarDays
                        size={16}
                        className="text-[#115e32]"
                      />

                      {formatDate(selectedNotice.start_date)}
                    </span>

                    <span className="text-slate-300">|</span>

                    <span>
                      {formatDate(selectedNotice.end_date)}
                    </span>
                  </div>
                )}
              </div>

              {/* Article Body */}

              <div className="p-6 sm:p-8">
                {loading ? (
                  <div className="space-y-4">
                    <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                    <div className="h-4 w-5/6 animate-pulse rounded bg-slate-100" />
                    <div className="h-4 w-4/6 animate-pulse rounded bg-slate-100" />
                    <div className="h-64 animate-pulse rounded-2xl bg-cyan-50" />
                  </div>
                ) : error ? (
                  <div className="rounded-2xl border border-red-100 bg-red-50 p-8 text-center">
                    <p className="font-semibold text-red-700">
                      {error}
                    </p>

                    <button
                      onClick={handlePost}
                      className="mt-5 inline-flex items-center gap-2 rounded-xl bg-[#115e32] px-5 py-3 text-white transition hover:bg-[#0d4826]"
                    >
                      <RefreshCw size={16} />

                      Try Again
                    </button>
                  </div>
                ) : selectedNotice ? (
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={selectedNotice.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.4 }}
                    >
                      {/* Description */}

                      {selectedNotice.description && (
                        <div
                          className="prose prose-slate max-w-none break-words
                          prose-headings:font-bold
                          prose-headings:text-[#014864]
                          prose-a:text-[#115e32]
                          prose-img:rounded-2xl
                          prose-img:shadow-md
                          prose-blockquote:border-l-[#115e32]
                          prose-li:marker:text-[#115e32]"
                          dangerouslySetInnerHTML={{
                            __html: textFormat(
                              selectedNotice.description
                            ),
                          }}
                        />
                      )}

                      {/* Image */}

                      {fileType === "image" && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.97 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.6 }}
                          className="mt-8 overflow-hidden rounded-2xl border border-cyan-100 bg-cyan-50"
                        >
                          <div className="flex items-center gap-2 border-b border-cyan-100 bg-white px-4 py-3 text-sm font-semibold text-[#014864]">
                            <ImageIcon
                              size={18}
                              className="text-[#115e32]"
                            />

                            Event Gallery
                          </div>

                          <img
                            src={imageUrl}
                            alt={decodeTitle(selectedNotice.name)}
                            className="h-auto max-h-[700px] w-full object-contain transition duration-500 hover:scale-[1.01]"
                          />
                        </motion.div>
                      )}

                      {/* PDF */}

                      {fileType === "pdf" && (
                        <div className="mt-8 overflow-hidden rounded-2xl border border-cyan-100 bg-white">
                          <div className="flex items-center justify-between gap-3 border-b border-cyan-100 bg-cyan-50 px-4 py-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-[#014864]">
                              <FileText
                                size={18}
                                className="text-[#115e32]"
                              />

                              Event Document
                            </div>

                            <a
                              href={imageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-xs font-bold text-[#115e32] hover:underline"
                            >
                              Open PDF

                              <ArrowUpRight size={15} />
                            </a>
                          </div>

                          <iframe
                            src={imageUrl}
                            title="Event PDF"
                            className="h-[500px] w-full md:h-[700px]"
                          />
                        </div>
                      )}

                      {/* Share / Event Footer */}

                      <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-slate-100 pt-6">
                        <div className="flex items-center gap-3">
                          <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-emerald-50">
                            <CalendarCheck
                              size={22}
                              className="text-[#115e32]"
                            />
                          </div>

                          <div>
                            <p className="text-sm font-bold text-[#014864]">
                              University of Creative Technology Chittagong
                            </p>

                            <p className="text-xs text-slate-400">
                              Events & Activities
                            </p>
                          </div>
                        </div>

                        <Link
                          to="/all-events"
                          className="inline-flex items-center gap-2 rounded-xl border border-cyan-100 bg-cyan-50 px-4 py-3 text-sm font-bold text-[#014864] transition hover:bg-[#6BCCF2]/30"
                        >
                          Browse Events

                          <ArrowRight size={16} />
                        </Link>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                ) : (
                  <p className="text-slate-500">
                    No event information available.
                  </p>
                )}
              </div>
            </div>
          </motion.div>

          {/* RIGHT: RECENT EVENTS */}

          <motion.aside
            initial={{ opacity: 0, x: 35 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.7 }}
            className="min-w-0 lg:sticky lg:top-24"
          >
            <div className="overflow-hidden rounded-3xl border border-cyan-100 bg-white shadow-xl shadow-[#014864]/5">
              {/* Sidebar Header */}

              <div className="relative overflow-hidden bg-gradient-to-r from-[#014864] to-[#115e32] p-6">
                <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-white/10 blur-2xl" />

                <div className="relative flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/20 bg-white/10 text-white">
                    <Newspaper size={23} />
                  </div>

                  <div>
                    <h2 className="text-xl font-bold text-white">
                      Recent Events
                    </h2>

                    <p className="mt-1 text-xs text-cyan-100/70">
                      Explore campus activities
                    </p>
                  </div>
                </div>
              </div>

              {/* Recent Events List */}

              <div className="p-4 sm:p-5">
                {recentLoading ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <EventSkeleton key={index} />
                    ))}
                  </div>
                ) : allnotice.length > 0 ? (
                  <div className="space-y-2">
                    {allnotice.slice(0, 15).map((n, index) => (
                      <motion.button
                        key={n.id}
                        type="button"
                        initial={{ opacity: 0, x: 15 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{
                          duration: 0.4,
                          delay: Math.min(index * 0.04, 0.3),
                        }}
                        onClick={() => selectEvent(n)}
                        className={`group w-full rounded-xl border p-4 text-left transition duration-300 ${
                          String(selectedNotice?.id) === String(n.id)
                            ? "border-emerald-200 bg-emerald-50 shadow-sm"
                            : "border-transparent bg-slate-50 hover:border-cyan-100 hover:bg-cyan-50"
                        }`}
                      >
                        <div className="mb-2 flex items-center justify-between gap-2">
                          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-400">
                            <CalendarDays
                              size={13}
                              className="text-[#115e32]"
                            />

                            {formatDate(n.start_date)}
                          </span>

                          <ArrowUpRight
                            size={15}
                            className="shrink-0 text-slate-300 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-[#115e32]"
                          />
                        </div>

                        <h3
                          className={`line-clamp-2 text-sm font-bold leading-6 transition ${
                            String(selectedNotice?.id) === String(n.id)
                              ? "text-[#115e32]"
                              : "text-[#014864] group-hover:text-[#115e32]"
                          }`}
                        >
                          {decodeTitle(n.name)}
                        </h3>

                        <div className="mt-3 flex items-center gap-2">
                          <span
                            className={`h-1 w-6 rounded-full transition ${
                              String(selectedNotice?.id) === String(n.id)
                                ? "bg-[#115e32]"
                                : "bg-cyan-200 group-hover:bg-[#115e32]"
                            }`}
                          />

                          <span className="text-xs text-slate-400">
                            {formatDate(n.end_date)}
                          </span>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                ) : (
                  <div className="py-10 text-center">
                    <CalendarDays
                      size={32}
                      className="mx-auto mb-3 text-slate-300"
                    />

                    <p className="text-sm text-slate-500">
                      No recent events available.
                    </p>
                  </div>
                )}

                {/* See All Events */}

                <div className="mt-5">
                  <Link
                    to="/all-events"
                    className="group flex w-full items-center justify-between rounded-xl bg-gradient-to-r from-[#014864] to-[#115e32] px-5 py-4 text-sm font-bold text-white shadow-md transition duration-300 hover:-translate-y-1 hover:shadow-lg"
                  >
                    <span>View All Events</span>

                    <ArrowRight
                      size={18}
                      className="transition group-hover:translate-x-1"
                    />
                  </Link>
                </div>
              </div>
            </div>

            {/* Sidebar CTA */}

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mt-6 overflow-hidden rounded-2xl border border-cyan-100 bg-gradient-to-br from-cyan-50 to-emerald-50 p-6"
            >
              <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-white shadow-sm">
                <Sparkles
                  size={22}
                  className="text-[#115e32]"
                />
              </div>

              <h3 className="text-lg font-bold text-[#014864]">
                Campus Life at UCTC
              </h3>

              <p className="mt-2 text-sm leading-6 text-slate-600">
                Discover exciting academic, cultural, and extracurricular
                activities happening across our campus.
              </p>

              <Link
                to="/all-events"
                className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#115e32] transition hover:gap-3"
              >
                Explore More

                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </motion.aside>
        </div>
      </section>
    </main>
  );
}

EventsDetails.propTypes = {
  logoSrc: PropTypes.string,
  accent: PropTypes.string,
  pageTitle: PropTypes.string,
};
